import { useNode, useEditor, Element } from "@craftjs/core";
import React, { useRef } from "react";
import { Box, Typography } from "@mui/material";
import { useResponsive } from "../../hooks/useResponsive";
import { Text } from "./Text";
import { selectedOutlineSx } from "../../config/settingsStyles";
import { TextField, TextareaField, SelectField, ColorField, CheckboxField, SliderField, SettingsSection } from "../ui/form/SettingsField";

export interface ContactInfoProps {
  clinicName: string;
  address: string;
  phone: string;
  email: string;
  workingHours?: string;
  mapEmbedUrl?: string;
  showMap?: boolean;
  backgroundColor?: string;
  textColor?: string;
  layout?: "vertical" | "horizontal" | "with-map";
  maxWidth?: number;
  paddingX?: number;
  paddingTop?: number;
  paddingBottom?: number;
}

export const ContactInfo = ({
  clinicName,
  address,
  phone,
  email,
  workingHours,
  mapEmbedUrl,
  showMap = false,
  backgroundColor = "#f9fafb",
  textColor = "#1f2937",
  layout = "vertical",
  maxWidth = 0,
  paddingX = 24,
  paddingTop = 24,
  paddingBottom = 24,
}: ContactInfoProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useResponsive(containerRef as React.RefObject<HTMLElement>);

  const {
    connectors: { connect, drag },
    selected,
    actions: { setProp },
  } = useNode((state) => ({
    selected: state.events.selected,
  }));

  const { inEditor } = useEditor((state) => ({
    inEditor: true,
  }));

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (inEditor) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const contactItems = (
    <Box
      sx={{
        display: "flex",
        flexDirection: layout === "horizontal" ? "row" : "column",
        flexWrap: layout === "horizontal" ? "wrap" : "nowrap",
        gap: layout === "horizontal" ? 4 : 2,
      }}
    >
      <Box sx={{ flex: layout === "horizontal" ? 1 : "none", minWidth: layout === "horizontal" ? 200 : "auto" }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <Typography sx={{ fontSize: "1.5rem" }}>📍</Typography>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Element
              id="contact-name"
              is={Text}
              text={clinicName}
              fontSize={24}
              fontWeight="bold"
              tagName="h3"
              color={textColor}
            />
            <Typography
              sx={{
                color: textColor,
                opacity: 0.8,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {address}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ flex: layout === "horizontal" ? 1 : "none", minWidth: layout === "horizontal" ? 200 : "auto" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography sx={{ fontSize: "1.5rem" }}>📞</Typography>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontWeight: 600, color: textColor }}>Phone</Typography>
            <Box
              component="a"
              href={`tel:${phone.replace(/\s/g, "")}`}
              onClick={handleLinkClick}
              sx={{
                color: "#2563eb",
                textDecoration: "none",
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {phone}
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ flex: layout === "horizontal" ? 1 : "none", minWidth: layout === "horizontal" ? 200 : "auto" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography sx={{ fontSize: "1.5rem" }}>✉️</Typography>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontWeight: 600, color: textColor }}>Email</Typography>
            <Box
              component="a"
              href={`mailto:${email}`}
              onClick={handleLinkClick}
              sx={{
                color: "#2563eb",
                textDecoration: "none",
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {email}
            </Box>
          </Box>
        </Box>
      </Box>

      {workingHours && (
        <Box sx={{ flex: layout === "horizontal" ? 1 : "none", minWidth: layout === "horizontal" ? 200 : "auto" }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <Typography sx={{ fontSize: "1.5rem" }}>🕐</Typography>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontWeight: 600, color: textColor }}>Working Hours</Typography>
              <Typography
                sx={{
                  color: textColor,
                  opacity: 0.8,
                  whiteSpace: "pre-line",
                  wordBreak: "break-word",
                }}
              >
                {workingHours}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );

  return (
    <Box
      ref={(ref: HTMLDivElement | null) => {
        if (ref) {
          connect(drag(ref));
          (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = ref;
        }
      }}
      sx={{
        px: `${paddingX}px`,
        pt: `${paddingTop}px`,
        pb: `${paddingBottom}px`,
        ...(maxWidth > 0 && { maxWidth: `${maxWidth}px`, mx: "auto" }),
        borderRadius: 2,
        bgcolor: backgroundColor,
        ...(selected && selectedOutlineSx),
      }}
    >
      {layout === "with-map" && showMap && mapEmbedUrl ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: 4,
          }}
        >
          <Box>{contactItems}</Box>
          <Box
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              height: isMobile ? "256px" : "auto",
            }}
          >
            <Box
              component="iframe"
              src={mapEmbedUrl}
              sx={{
                width: "100%",
                height: "100%",
                border: 0,
                minHeight: 300,
              }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </Box>
        </Box>
      ) : (
        contactItems
      )}
    </Box>
  );
};

export const ContactInfoSettings = () => {
  const {
    actions: { setProp },
    address,
    phone,
    email,
    workingHours,
    mapEmbedUrl,
    showMap,
    backgroundColor,
    textColor,
    layout,
    maxWidth,
    paddingX,
    paddingTop,
    paddingBottom,
  } = useNode((node) => ({
    address: node.data.props.address,
    phone: node.data.props.phone,
    email: node.data.props.email,
    workingHours: node.data.props.workingHours,
    mapEmbedUrl: node.data.props.mapEmbedUrl,
    showMap: node.data.props.showMap,
    backgroundColor: node.data.props.backgroundColor,
    textColor: node.data.props.textColor,
    layout: node.data.props.layout,
    maxWidth: node.data.props.maxWidth,
    paddingX: node.data.props.paddingX,
    paddingTop: node.data.props.paddingTop,
    paddingBottom: node.data.props.paddingBottom,
  }));

  return (
    <SettingsSection>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
        💡 คลิกที่ตัวอักษรบน canvas เพื่อแก้ไข
      </Typography>

      <TextareaField
        label="Address"
        value={address}
        onChange={(v) => setProp((props: ContactInfoProps) => (props.address = v))}
        rows={2}
      />

      <TextField
        label="Phone"
        value={phone}
        onChange={(v) => setProp((props: ContactInfoProps) => (props.phone = v))}
      />

      <TextField
        label="Email"
        value={email}
        onChange={(v) => setProp((props: ContactInfoProps) => (props.email = v))}
      />

      <TextareaField
        label="Working Hours"
        value={workingHours || ""}
        onChange={(v) => setProp((props: ContactInfoProps) => (props.workingHours = v || undefined))}
        rows={3}
        placeholder="Mon-Fri: 9:00 AM - 6:00 PM&#10;Sat: 9:00 AM - 1:00 PM"
      />

      <SelectField
        label="Layout"
        value={layout}
        onChange={(v) => setProp((props: ContactInfoProps) => (props.layout = v as ContactInfoProps["layout"]))}
        options={[
          { value: "vertical", label: "Vertical" },
          { value: "horizontal", label: "Horizontal" },
          { value: "with-map", label: "With Map" },
        ]}
      />

      {layout === "with-map" && (
        <>
          <CheckboxField
            label="Show Map"
            checked={showMap}
            onChange={(v) => setProp((props: ContactInfoProps) => (props.showMap = v))}
          />

          {showMap && (
            <TextField
              label="Google Maps Embed URL"
              value={mapEmbedUrl || ""}
              onChange={(v) => setProp((props: ContactInfoProps) => (props.mapEmbedUrl = v || undefined))}
              placeholder="https://www.google.com/maps/embed?..."
            />
          )}
        </>
      )}

      <SliderField
        label="Max Width"
        value={maxWidth}
        onChange={(v) => setProp((props: ContactInfoProps) => (props.maxWidth = v))}
        min={0}
        max={1600}
        unit="px"
      />

      <SliderField
        label="Padding X"
        value={paddingX}
        onChange={(v) => setProp((props: ContactInfoProps) => (props.paddingX = v))}
        min={0}
        max={120}
        unit="px"
      />

      <SliderField
        label="Padding Top"
        value={paddingTop}
        onChange={(v) => setProp((props: ContactInfoProps) => (props.paddingTop = v))}
        min={0}
        max={120}
        unit="px"
      />

      <SliderField
        label="Padding Bottom"
        value={paddingBottom}
        onChange={(v) => setProp((props: ContactInfoProps) => (props.paddingBottom = v))}
        min={0}
        max={120}
        unit="px"
      />

      <ColorField
        label="Background Color"
        value={backgroundColor}
        onChange={(v) => setProp((props: ContactInfoProps) => (props.backgroundColor = v))}
      />

      <ColorField
        label="Text Color"
        value={textColor}
        onChange={(v) => setProp((props: ContactInfoProps) => (props.textColor = v))}
      />
    </SettingsSection>
  );
};

ContactInfo.craft = {
  displayName: "Contact Info",
  isCanvas: true,
  props: {
    clinicName: "Premium Clinic",
    address: "123 Medical Center Road, Bangkok 10110",
    phone: "02-123-4567",
    email: "contact@clinic.com",
    workingHours: "Mon-Fri: 9:00 AM - 6:00 PM\nSat: 9:00 AM - 1:00 PM\nSun: Closed",
    showMap: false,
    backgroundColor: "#f9fafb",
    textColor: "#1f2937",
    layout: "vertical",
    maxWidth: 0,
    paddingX: 24,
    paddingTop: 24,
    paddingBottom: 24,
  },
  rules: {
    canMoveIn: () => false,
    canMoveOut: () => false,
  },
  related: {
    settings: ContactInfoSettings,
  },
};
